import React, { useState, useEffect } from 'react';

/**
 * AdminTable — reusable data table used across all admin list pages.
 *
 * Props:
 *   tableHeaders  {Array}    Column definitions: [{ Header, accessor, Cell? }]
 *   tableData     {Array}    Array of row data objects
 *   showCheckbox  {Boolean}  Show a checkbox column for bulk selection (default false)
 *   onSelectionChange {Function} Called with array of selected row IDs when selection changes
 *   rowIdKey      {String}   Field name used as the unique row identifier (default 'id')
 */
const AdminTable = ({
  tableHeaders = [],
  tableData = [],
  showCheckbox = false,
  onSelectionChange,
  rowIdKey = 'id',
}) => {
  const [selectedIds, setSelectedIds] = useState([]);

  // Notify parent whenever selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedIds);
    }
  }, [selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset selection when data changes (e.g. page change)
  useEffect(() => {
    setSelectedIds([]);
  }, [tableData]);

  const allSelected =
    tableData.length > 0 &&
    tableData.every((row) => selectedIds.includes(row[rowIdKey]));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(tableData.map((row) => row[rowIdKey]));
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  return (
    <div className="table-responsive">
      <table className="table align-middle">
        <thead>
          <tr>
            {showCheckbox && (
              <th style={{ width: 50 }}>
                <div className="custom-control custom-checkbox ml-2">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="selectAll"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />
                  <label className="custom-control-label" htmlFor="selectAll">
                    <span className="sr-only">Select all</span>
                  </label>
                </div>
              </th>
            )}
            {tableHeaders.map((col, idx) => (
              <th key={col.accessor || idx} style={{ border: 'none' }}>{col.Header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.length === 0 ? (
            <tr>
              <td
                colSpan={tableHeaders.length + (showCheckbox ? 1 : 0)}
                className="text-center py-5 text-muted"
              >
                <div className="mb-2" style={{ fontSize: '2rem', opacity: 0.3 }}>📂</div>
                No records found in this section.
              </td>
            </tr>
          ) : (
            tableData.map((row, rowIdx) => {
              const rowId = row[rowIdKey];
              const isChecked = selectedIds.includes(rowId);
              return (
                <tr key={rowId ?? rowIdx}>
                  {showCheckbox && (
                    <td>
                      <div className="custom-control custom-checkbox ml-2">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id={`check-${rowId}`}
                          checked={isChecked}
                          onChange={() => toggleSelectRow(rowId)}
                        />
                        <label className="custom-control-label" htmlFor={`check-${rowId}`}>
                          <span className="sr-only">Select row</span>
                        </label>
                      </div>
                    </td>
                  )}
                  {tableHeaders.map((col, colIdx) => (
                    <td key={col.accessor || colIdx} style={{ fontSize: '0.9rem' }}>
                      {col.Cell
                        ? col.Cell({ row: { original: row, index: rowIdx } })
                        : row[col.accessor] ?? '—'}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AdminTable;
